import { ShoppingBag, Search, User, Menu, X, Heart, ChevronDown, ChevronLeft } from "lucide-react";
import accessibilityIcon from "@/assets/accessibility-icon.svg";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CartDrawer } from "./CartDrawer";
import { SearchDialog } from "./SearchDialog";
import { PromoBanner } from "./PromoBanner";
import { useWishlistStore } from "@/store/wishlistStore";
import { useStaticTranslation } from "@/lib/staticTranslations";

import { useLanguage } from "@/i18n";
import logo from "@/assets/logo-alice-new.png";
import { fetchShopifyCollections, ShopifyCollection } from "@/lib/shopify";
import { translateMenu, loadMenuTranslations } from "@/lib/menuTranslations";
import { getTranslateLang } from "@/lib/translateUtils";

interface HeaderProps {
  forceShowShadow?: boolean;
  activeCategoryHandle?: string | null;
}

const Header = ({ forceShowShadow = false, activeCategoryHandle }: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const [isMenuLoading, setIsMenuLoading] = useState(true);
  const [menuSections, setMenuSections] = useState<Array<{ 
    title: string; 
    directCollectionHandle?: string;
    titleCollectionHandles?: string[];
    image?: { url: string; altText: string | null } | null;
    subsections: Array<{
      title: string;
      collections: ShopifyCollection[];
      collectionTitles?: Record<string, string>;
    }>;
  }>>([]);
  const { items: wishlistItems, localItems: localWishlistItems } = useWishlistStore();
  const totalWishlistCount = wishlistItems.length + localWishlistItems.length;
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [isServiceMenuHovered, setIsServiceMenuHovered] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  
  // Mobile drill-down navigation state
  const [mobileActiveSection, setMobileActiveSection] = useState<string | null>(null);
  const [mobileActiveSubsection, setMobileActiveSubsection] = useState<{ sectionTitle: string; subsectionIndex: number } | null>(null);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getTranslateLang);

  // Measure full header height (promo banner + header) with ResizeObserver for dynamic updates
  useLayoutEffect(() => {
    const measureHeader = () => {
      if (headerContainerRef.current) {
        const height = headerContainerRef.current.getBoundingClientRect().height;
        setHeaderHeight(height);
      }
    };
    
    measureHeader();
    
    // Use ResizeObserver for dynamic height changes (promo banner load, font load, etc.)
    const resizeObserver = new ResizeObserver(() => {
      measureHeader();
    });
    
    if (headerContainerRef.current) {
      resizeObserver.observe(headerContainerRef.current);
    }
    
    window.addEventListener('resize', measureHeader);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureHeader);
    };
  }, []);

  const { t: th } = useStaticTranslation("header");

  // Service menu links
  const serviceMenuLinks = [
    { title: th('contactUs', 'צור קשר'), href: '/contact', requiresAuth: false },
    { title: th('faq', 'שאלות נפוצות'), href: '/faq', requiresAuth: false },
    { title: th('sizeGuide', 'מדריך מידות'), href: '/ring-size-guide', requiresAuth: false },
    { title: 'Alice Trade In', href: '/trade-in', requiresAuth: false },
    { title: th('trackRepairs', 'מעקב אחרי תיקונים'), href: '/track-order', requiresAuth: false },
  ];

  // Shorthand for menu translation (currentLang in dependency ensures re-render)
  const tm = (text: string) => translateMenu(text);

  const handleAboutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on homepage, just scroll
      const aboutSection = document.getElementById('about');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage with hash
      navigate('/#about');
    }
  };

  const handleMenuToggle = () => {
    if (isMenuOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
        // Reset mobile navigation state
        setMobileActiveSection(null);
        setMobileActiveSubsection(null);
        setMobileServiceOpen(false);
      }, 200);
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleMobileBack = () => {
    if (mobileActiveSubsection) {
      // If the section has only 1 subsection, go back to main menu (we skipped subsection level)
      const section = menuSections.find(s => s.title === mobileActiveSubsection.sectionTitle);
      if (section && section.subsections.length <= 1) {
        setMobileActiveSubsection(null);
        setMobileActiveSection(null);
      } else {
        setMobileActiveSubsection(null);
      }
    } else if (mobileActiveSection) {
      setMobileActiveSection(null);
    }
  };

  useEffect(() => {
    let ticking = false;

    const updateScrolled = () => {
      const nextIsScrolled = window.scrollY > 10;
      setIsScrolled((prev) => (prev === nextIsScrolled ? prev : nextIsScrolled));
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateScrolled();
        ticking = false;
      });
    };

    updateScrolled();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load menu translations and track language changes
  useEffect(() => {
    loadMenuTranslations();
    
    // Poll for language changes (Google Translate updates localStorage)
    const interval = setInterval(() => {
      const lang = getTranslateLang();
      setCurrentLang(prev => prev !== lang ? lang : prev);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadCollections = async () => {
      // Try to load from cache first for instant display
      const cachedMenu = sessionStorage.getItem('header_menu_cache');
      const cachedCollections = sessionStorage.getItem('header_collections_cache');
      
      if (cachedMenu && cachedCollections) {
        try {
          const parsedMenu = JSON.parse(cachedMenu);
          const parsedCollections = JSON.parse(cachedCollections);
          setMenuSections(parsedMenu);
          setCollections(parsedCollections);
          setIsMenuLoading(false);
        } catch (e) {
          // Cache parse error, continue with fresh load
        }
      }

      // Load fresh data in background
      const result = await fetchShopifyCollections();
      const collectionsData = result.collections;
      setCollections(collectionsData);
      
      // Cache collections
      try {
        sessionStorage.setItem('header_collections_cache', JSON.stringify(collectionsData));
      } catch (e) {}

      // Load header settings from database
      const { data: headerSetting } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'header_collections')
        .maybeSingle();

      if (headerSetting?.setting_value) {
          const value = headerSetting.setting_value as { 
          sections?: Array<{ 
            title: string; 
            directCollectionHandle?: string;
            titleCollectionHandle?: string;
            titleCollectionHandles?: string[];
            imageUrl?: string;
            subsections: Array<{
              title: string;
              collections: string[];
              collectionTitles?: Record<string, string>;
            }>;
          }> 
        };
        if (value.sections && value.sections.length > 0) {
          const sections = value.sections.map(section => {
            // Use custom image if available, otherwise fallback to first collection's image
            let sectionImage = null;
            
            if (section.imageUrl) {
              // Use custom uploaded image
              sectionImage = { url: section.imageUrl, altText: section.title };
            } else {
              // Fallback: Get the first collection's image from the first subsection
              const firstSubsection = section.subsections?.[0];
              const firstCollectionHandle = firstSubsection?.collections?.[0];
              const firstCollection = firstCollectionHandle ? collectionsData.find(c => c.node.handle === firstCollectionHandle) : null;
              sectionImage = firstCollection?.node.image || null;
            }
            
            return {
              title: section.title,
              directCollectionHandle: section.directCollectionHandle,
              // Backward compat: convert old titleCollectionHandle to titleCollectionHandles array
              titleCollectionHandles: section.titleCollectionHandles || (section.titleCollectionHandle ? [section.titleCollectionHandle] : undefined),
              imageUrl: section.imageUrl,
              image: sectionImage,
              subsections: section.subsections.map(subsection => ({
                title: subsection.title,
                collections: subsection.collections
                  .map(handle => collectionsData.find(c => c.node.handle === handle))
                  .filter(Boolean),
                collectionTitles: subsection.collectionTitles,
              }))
            };
          });
          setMenuSections(sections as any);
          
          // Cache menu sections
          try {
            sessionStorage.setItem('header_menu_cache', JSON.stringify(sections));
          } catch (e) {}
        }
      }
      setIsMenuLoading(false);
    };
    loadCollections();
  }, []);

  return (
    <>
    <div ref={headerContainerRef} className="fixed top-0 left-0 right-0 z-50">
      <PromoBanner />
      <header 
        className={`w-full bg-background border-b border-border/50 transition-shadow duration-300 ${
          isScrolled || forceShowShadow ? 'shadow-md' : ''
        }`} 
        dir="rtl"
    >
      <div className="w-full px-4 lg:px-8">
        {/* Top Row - Logo and Icons */}
        <div className="flex h-20 items-center justify-between">
          {/* Mobile: Menu and Search icons on left */}
          <div className="flex lg:hidden items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-transparent"
              onClick={handleMenuToggle}
            >
              {isMenuOpen ? <X className="h-5 w-5 text-[#7e6b44]" /> : <Menu className="h-5 w-5 text-[#7e6b44]" />}
            </Button>
            <SearchDialog />
          </div>

          {/* Logo - Center on mobile, Right on desktop */}
          <Link to="/" className="flex items-center lg:order-first">
            <img src={logo} alt="ALICE Jewellery | Watches" className="h-12 w-auto" />
          </Link>

          {/* Mobile: Accessibility and Cart on right */}
          <div className="flex lg:hidden items-center gap-1">
            
            <Button
              id="accessibility-btn"
              variant="ghost"
              size="icon"
              className="hover:bg-transparent"
              onClick={() => {
                const uw = (window as any).UserWay;
                if (uw?.widgetOpen) uw.widgetOpen();
              }}
              aria-label="נגישות"
            >
              <img src={accessibilityIcon} alt="נגישות" className="h-6 w-6" style={{ filter: 'brightness(0) saturate(100%) invert(43%) sepia(12%) saturate(1200%) hue-rotate(10deg) brightness(92%) contrast(88%)' }} />
            </Button>
            <CartDrawer />
          </div>

          {/* Desktop Icons - Left Side */}
          <div className="hidden lg:flex items-center gap-1">
            
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-transparent"
              asChild
            >
              <Link to={isLoggedIn ? "/profile" : "/auth"} aria-label={isLoggedIn ? th("personalArea", "האזור האישי") : th("login", "התחברות")}>
                <User className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-transparent relative"
              asChild
            >
              <Link to="/wishlist" aria-label={th("wishlist", "רשימת המועדפים")}>
                <Heart className="h-5 w-5 text-primary" strokeWidth={1.5} />
                {totalWishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {totalWishlistCount}
                  </span>
                )}
              </Link>
            </Button>
            <Button
              id="accessibility-btn"
              variant="ghost"
              size="icon"
              className="hover:bg-transparent"
              onClick={() => {
                const uw = (window as any).UserWay;
                if (uw?.widgetOpen) uw.widgetOpen();
              }}
              aria-label="נגישות"
            >
              <img src={accessibilityIcon} alt="נגישות" className="h-6 w-6" style={{ filter: 'brightness(0) saturate(100%) invert(43%) sepia(12%) saturate(1200%) hue-rotate(10deg) brightness(92%) contrast(88%)' }} />
            </Button>
            <SearchDialog />
            <CartDrawer />
          </div>
        </div>
      </div>

      {/* Navigation - Desktop with Mega Menu */}
      <nav className="hidden lg:block border-t border-border/50 relative">
        <div className="flex items-center justify-center gap-8 h-12 px-4 lg:px-8 text-base">
          <Link 
            to="/" 
            className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname === '/' ? 'font-bold' : 'hover:after:scale-x-100 hover:after:origin-bottom-right'}`}
            translate="no"
          >
            {tm('ראשי')}
          </Link>
          {isMenuLoading && menuSections.length === 0 && (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 w-16 bg-primary/10 rounded animate-pulse" />
              ))}
            </>
          )}
          {menuSections.map((section) => {
            // Use activeCategoryHandle from props if provided, otherwise fall back to URL
            const currentCategoryHandle = activeCategoryHandle !== undefined 
              ? activeCategoryHandle
              : (location.pathname.startsWith('/collections/') 
                  ? decodeURIComponent(location.pathname.replace('/collections/', ''))
                  : null);
            
            // If activeCategoryHandle is explicitly null, no highlighting
            const titleFilterPath = section.titleCollectionHandles?.length 
              ? `/collections/all?filter=${section.titleCollectionHandles.join(',')}`
              : null;
            const isSectionActive = currentCategoryHandle === null 
              ? false
              : section.directCollectionHandle 
                ? currentCategoryHandle === section.directCollectionHandle
                : (section.titleCollectionHandles?.length && section.titleCollectionHandles.includes(currentCategoryHandle!))
                  ? true
                  : section.subsections.some(sub => 
                      sub.collections.some(col => col.node.handle === currentCategoryHandle)
                    );

            return (
              <div 
                key={section.title}
                className="relative group"
                onMouseEnter={() => !section.directCollectionHandle && setHoveredSection(section.title)}
                onMouseLeave={() => setHoveredSection(null)}
              >
                {section.directCollectionHandle ? (
                  // Direct link - no dropdown
                  <Link
                    to={`/collections/${section.directCollectionHandle}`}
                    className={`text-base transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 h-12 flex items-center ${
                      isSectionActive 
                        ? 'text-primary font-semibold after:scale-x-100' 
                        : 'text-primary hover:text-primary/80 after:scale-x-0 hover:after:scale-x-100 hover:after:origin-bottom-right'
                    }`}
                  >
                    {tm(section.title)}
                  </Link>
                ) : section.titleCollectionHandles?.length ? (
                  // Title links to filtered collections, but hover still opens mega menu
                  <Link
                    to={`/collections/all?filter=${section.titleCollectionHandles.join(',')}`}
                    className={`text-base transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 h-12 flex items-center gap-1 ${
                      isSectionActive 
                        ? 'text-primary font-semibold after:scale-x-100' 
                        : 'text-primary hover:text-primary/80 after:scale-x-0 hover:after:scale-x-100 hover:after:origin-bottom-right'
                    }`}
                  >
                    {tm(section.title)}
                    {section.subsections.length > 0 && (
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${hoveredSection === section.title ? 'rotate-180' : ''}`} />
                    )}
                  </Link>
                ) : (
                  <button
                    className={`text-base transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 h-12 flex items-center gap-1 ${
                      isSectionActive 
                        ? 'text-primary font-semibold after:scale-x-100' 
                        : 'text-primary hover:text-primary/80 after:scale-x-0 hover:after:scale-x-100 hover:after:origin-bottom-right'
                    }`}
                  >
                    {tm(section.title)}
                    {section.subsections.length > 0 && (
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${hoveredSection === section.title ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
          <Link 
            to="/blog"
            className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname.startsWith('/blog') ? 'font-bold' : 'hover:after:scale-x-100 hover:after:origin-bottom-right'}`}
            translate="no"
          >
            {tm('בלוג')}
          </Link>
          <Link 
            to="/about"
            className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname === '/about' ? 'font-bold' : 'hover:after:scale-x-100 hover:after:origin-bottom-right'}`}
            translate="no"
          >
            {tm('אודות')}
          </Link>
          <Link 
            to="/gift-card"
            className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname === '/gift-card' ? 'font-bold' : 'hover:after:scale-x-100 hover:after:origin-bottom-right'}`}
            translate="no"
          >
            GiftCard
          </Link>
          <div 
            className="relative notranslate"
            translate="no"
            onMouseEnter={() => setIsServiceMenuHovered(true)}
            onMouseLeave={() => setIsServiceMenuHovered(false)}
          >
            <button
              className={`text-base text-primary hover:text-primary/80 transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-right h-12 flex items-center gap-1 ${
                ['/contact', '/faq', '/ring-size-guide', '/accessibility'].includes(location.pathname) ? 'font-bold' : ''
              }`}
            >
              {th('customerService', 'שירות לקוחות')}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isServiceMenuHovered ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Service Menu Dropdown */}
            <div 
              className={`absolute top-full right-0 min-w-[200px] bg-background border border-border/30 shadow-lg z-50 overflow-hidden transition-all duration-200 ${
                isServiceMenuHovered
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
            >
              <div className="py-3 px-1">
                {serviceMenuLinks.map((link, index) => (
                  <Link
                    key={link.href}
                    to={link.requiresAuth && !isLoggedIn ? '/auth' : link.href}
                    onClick={() => {
                      if (link.requiresAuth && !isLoggedIn) {
                        navigate('/auth', { state: { redirectTo: link.href } });
                      }
                      setIsServiceMenuHovered(false);
                    }}
                    className={`block px-5 py-2.5 text-sm transition-all duration-150 ${
                      location.pathname === link.href.split('?')[0] 
                        ? 'text-primary font-medium bg-champagne/50' 
                        : 'text-foreground/70 hover:text-primary hover:bg-champagne/30'
                    }`}
                  >
                    {link.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Mega Menu - Always at same position */}
        {menuSections.map((section) => (
          !section.directCollectionHandle && section.subsections.length > 0 && (
            <div 
              key={`mega-${section.title}`}
              className={`absolute top-full left-1/2 -translate-x-1/2 w-[950px] bg-background border border-border/40 shadow-xl z-50 overflow-hidden transition-all duration-300 ${
                hoveredSection === section.title
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 -translate-y-3 pointer-events-none'
              }`}
              onMouseEnter={() => setHoveredSection(section.title)}
              onMouseLeave={() => setHoveredSection(null)}
            >
              <div className="flex h-[340px]">
                {/* Category Image - Left Side */}
                {section.image && (
                  <Link 
                    to={section.titleCollectionHandles?.length 
                      ? `/collections/all?filter=${section.titleCollectionHandles.join(',')}`
                      : `/collections/${section.subsections[0]?.collections[0]?.node.handle || ''}`
                    }
                    onClick={() => setHoveredSection(null)}
                    className="w-[300px] relative overflow-hidden flex-shrink-0 group cursor-pointer"
                  >
                    <img 
                      src={section.image.url} 
                      alt={section.image.altText || section.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 right-0 left-0 p-6">
                      <span className="text-white text-xl font-bold">{tm(section.title)}</span>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-white/80 text-sm">{tm('לצפייה בקולקציה')}</span>
                        <ChevronLeft className="w-4 h-4 text-white/80 group-hover:-translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                )}
                
                {/* Subsections Grid */}
                <div className="flex-1 bg-champagne/20">
                  <div className="h-full flex">
                    {section.subsections.map((subsection, idx) => (
                      <div 
                        key={idx} 
                        className={`flex-1 py-6 px-5 ${
                          idx !== section.subsections.length - 1 ? 'border-l border-border/30' : ''
                        }`}
                      >
                        {subsection.title && (
                          <div className="mb-4 pb-3 border-b border-primary/20">
                            <h3 className="text-sm font-bold text-primary uppercase tracking-wider">
                              {tm(subsection.title)}
                            </h3>
                          </div>
                        )}
                        <div className="space-y-0.5">
                          {subsection.collections.map((collection) => {
                            const isCollectionActive = decodeURIComponent(location.pathname) === `/collections/${collection.node.handle}`;
                            return (
                              <Link
                                key={collection.node.id}
                                to={`/collections/${collection.node.handle}`}
                                onClick={() => setHoveredSection(null)}
                                className={`flex items-center gap-2 py-2 px-3 text-sm transition-all duration-200 group/item ${
                                  isCollectionActive 
                                    ? 'text-primary font-semibold bg-primary/10' 
                                    : 'text-foreground/70 hover:text-primary hover:bg-champagne/50'
                                }`}
                              >
                                {isCollectionActive && (
                                  <span className="w-1 h-4 bg-primary flex-shrink-0" />
                                )}
                                <span className={`${!isCollectionActive ? 'group-hover/item:translate-x-[-4px] transition-transform duration-200' : ''}`}>
                                  {subsection.collectionTitles?.[collection.node.handle] || tm(collection.node.title)}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
        ))}
      </nav>

      {/* Mobile Menu Backdrop */}
      {isMenuOpen && (
        <div 
          className={`lg:hidden fixed inset-0 bg-background/80 backdrop-blur-md z-40 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
          style={{ top: `${headerHeight}px` }}
          onClick={handleMenuToggle}
        />
      )}

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <nav 
          className={`lg:hidden border-t fixed left-0 right-0 bottom-0 z-50 bg-background ${isClosing ? 'animate-fade-out' : 'animate-slide-down'}`}
          style={{ top: `${headerHeight}px` }}
        >
        
          <div className="relative h-full overflow-hidden">
            {/* Main Menu */}
            <div 
              className={`flex flex-col text-center px-4 py-4 transition-all duration-300 overflow-y-auto ${
                mobileActiveSection || mobileServiceOpen ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-100 translate-x-0 h-full'
              }`}
            >
              <Link 
                to="/" 
                onClick={handleMenuToggle}
                className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${location.pathname === '/' ? 'font-semibold text-primary' : ''}`}
                style={{ fontWeight: 400 }}
                translate="no"
              >
                <span className="w-5 flex-shrink-0 flex justify-center">{location.pathname === '/' ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                <span className="flex-1 text-center">{tm('ראשי')}</span>
                <span className="w-5 flex-shrink-0" />
              </Link>
              {isMenuLoading && menuSections.length === 0 && (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="border-b border-border/30 py-3">
                      <div className="h-4 w-24 bg-primary/10 rounded animate-pulse mx-auto" />
                    </div>
                  ))}
                </>
              )}
              {menuSections.map((section) => {
                const currentPath = decodeURIComponent(location.pathname);
                const isSectionActive = section.directCollectionHandle 
                  ? currentPath === `/collections/${section.directCollectionHandle}`
                  : (section.titleCollectionHandles?.length && section.titleCollectionHandles.some(h => currentPath === `/collections/${h}`))
                    ? true
                    : section.subsections.some(sub => 
                        sub.collections.some(col => currentPath === `/collections/${col.node.handle}`)
                      );
                
                return (
                  <div key={section.title} className="border-b border-border/30">
                    {section.directCollectionHandle ? (
                      <Link 
                        to={`/collections/${section.directCollectionHandle}`}
                        onClick={handleMenuToggle}
                        className={`text-sm text-primary/80 hover:text-primary flex items-center py-3 ${isSectionActive ? 'font-semibold text-primary' : ''}`}
                        style={{ fontWeight: 400 }}
                      >
                        <span className="w-5 flex-shrink-0 flex justify-center">{isSectionActive ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                        <span className="flex-1 text-center">{tm(section.title)}</span>
                        <span className="w-5 flex-shrink-0" />
                      </Link>
                    ) : section.titleCollectionHandles?.length ? (
                      // Title opens drill-down subsections, same as regular sections
                      <button 
                        onClick={() => {
                          if (section.subsections.length === 1 && section.subsections[0].collections.length === 1) {
                            navigate(`/collections/${section.subsections[0].collections[0].node.handle}`);
                            handleMenuToggle();
                          } else if (section.subsections.length === 1) {
                            setMobileActiveSection(section.title);
                            setMobileActiveSubsection({ sectionTitle: section.title, subsectionIndex: 0 });
                          } else {
                            setMobileActiveSection(section.title);
                          }
                        }}
                        className={`text-sm text-primary/80 hover:text-primary flex items-center w-full py-3 ${isSectionActive ? 'font-semibold text-primary' : ''}`}
                        style={{ fontWeight: 400 }}
                      >
                        <span className="w-5 flex-shrink-0 flex justify-center">{isSectionActive ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                        <span className="flex-1 text-center">{tm(section.title)}</span>
                        <span className="w-5 flex-shrink-0 flex justify-center">
                          {section.subsections.length > 0 && (
                            <ChevronLeft className="h-4 w-4" />
                          )}
                        </span>
                      </button>
                    ) : (
                      <button 
                        onClick={() => {
                          if (section.subsections.length === 1 && section.subsections[0].collections.length === 1) {
                            navigate(`/collections/${section.subsections[0].collections[0].node.handle}`);
                            handleMenuToggle();
                          } 
                          else if (section.subsections.length === 1) {
                            setMobileActiveSection(section.title);
                            setMobileActiveSubsection({ sectionTitle: section.title, subsectionIndex: 0 });
                          }
                          else {
                            setMobileActiveSection(section.title);
                          }
                        }}
                        className={`text-sm text-primary/80 hover:text-primary flex items-center w-full py-3 ${isSectionActive ? 'font-semibold text-primary' : ''}`}
                        style={{ fontWeight: 400 }}
                      >
                        <span className="w-5 flex-shrink-0 flex justify-center">{isSectionActive ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                        <span className="flex-1 text-center">{tm(section.title)}</span>
                        <span className="w-5 flex-shrink-0 flex justify-center">
                          {section.subsections.length > 0 && (
                            <ChevronLeft className="h-4 w-4" />
                          )}
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
              <Link 
                to="/blog"
                onClick={handleMenuToggle}
                className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${location.pathname.startsWith('/blog') ? 'font-semibold text-primary' : ''}`}
                style={{ fontWeight: 400 }}
                translate="no"
              >
                <span className="w-5 flex-shrink-0 flex justify-center">{location.pathname.startsWith('/blog') ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                <span className="flex-1 text-center">{tm('בלוג')}</span>
                <span className="w-5 flex-shrink-0" />
              </Link>
              <Link 
                to="/about"
                className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${location.pathname === '/about' ? 'font-semibold text-primary' : ''}`}
                style={{ fontWeight: 400 }}
                translate="no"
              >
                <span className="w-5 flex-shrink-0 flex justify-center">{location.pathname === '/about' ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                <span className="flex-1 text-center">{tm('אודות')}</span>
                <span className="w-5 flex-shrink-0" />
              </Link>
              <Link 
                to="/gift-card"
                onClick={handleMenuToggle}
                className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${location.pathname === '/gift-card' ? 'font-semibold text-primary' : ''}`}
                style={{ fontWeight: 400 }}
                translate="no"
              >
                <span className="w-5 flex-shrink-0 flex justify-center">{location.pathname === '/gift-card' ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                <span className="flex-1 text-center">{`GiftCard`}</span>
                <span className="w-5 flex-shrink-0" />
              </Link>
              <button 
                onClick={() => setMobileServiceOpen(true)}
                className={`notranslate text-sm text-primary/80 hover:text-primary flex items-center w-full py-3 border-b border-border/30 ${
                  ['/contact', '/faq', '/ring-size-guide', '/accessibility'].includes(location.pathname) ? 'font-semibold text-primary' : ''
                }`}
                style={{ fontWeight: 400 }}
                translate="no"
              >
                <span className="w-5 flex-shrink-0 flex justify-center">{(['/contact', '/faq', '/ring-size-guide', '/accessibility'].includes(location.pathname)) ? <span className="h-4 w-[2px] bg-primary" /> : null}</span>
                <span className="flex-1 text-center">{th('customerService', 'שירות לקוחות')}</span>
                <span className="w-5 flex-shrink-0 flex justify-center">
                  <ChevronLeft className="h-4 w-4" />
                </span>
              </button>
              
              {/* Mobile Icons - Wishlist and Login */}
              <div className="flex items-center justify-center gap-4 pt-4 border-t">
                <Button variant="ghost" size="icon" className="hover:bg-transparent relative" asChild>
                  <Link to="/wishlist" onClick={handleMenuToggle}>
                    <Heart className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    {totalWishlistCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {totalWishlistCount}
                      </span>
                    )}
                  </Link>
                </Button>
                <Button variant="ghost" size="icon" className="hover:bg-transparent relative" asChild>
                  <Link to={isLoggedIn ? "/profile" : "/auth"} onClick={handleMenuToggle} aria-label={isLoggedIn ? th("personalArea", "האזור האישי") : th("login", "התחברות")}>
                    <User className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Mobile Service Menu Submenu */}
            <div 
              className={`flex flex-col px-4 py-4 transition-all duration-300 absolute inset-0 bg-background overflow-y-auto notranslate ${
                mobileServiceOpen
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-full pointer-events-none'
              }`}
            >
              <button 
                onClick={() => setMobileServiceOpen(false)}
                className="flex items-center gap-2 text-sm text-primary py-3 border-b border-border/30"
                style={{ fontWeight: 400 }}
              >
                <ChevronLeft className="h-4 w-4 rotate-180" />
                <span>{tm('חזרה')}</span>
              </button>
              <div className="text-center py-3 border-b border-border/30 text-primary font-semibold text-base">
                {th('customerService', 'שירות לקוחות')}
              </div>
              {serviceMenuLinks.map((link) => (
                <Link 
                  key={link.href}
                  to={link.requiresAuth && !isLoggedIn ? '/auth' : link.href}
                  onClick={() => {
                    if (link.requiresAuth && !isLoggedIn) {
                      navigate('/auth', { state: { redirectTo: link.href } });
                    }
                    setMobileServiceOpen(false);
                    handleMenuToggle();
                  }}
                  className={`py-3 border-b border-border/30 text-sm text-primary/80 hover:text-primary flex items-center ${
                    location.pathname === link.href.split('?')[0] ? 'font-semibold text-primary' : ''
                  }`}
                  style={{ fontWeight: 400 }}
                >
                  <span className="w-5 flex-shrink-0" />
                  <span className="flex-1 text-center">{link.title}</span>
                  <span className="w-5 flex-shrink-0" />
                </Link>
              ))}
            </div>

            {/* Section Submenu */}
            {menuSections.map((section) => (
              <div 
                key={`submenu-${section.title}`}
                className={`flex flex-col px-4 py-4 transition-all duration-300 absolute inset-0 bg-background overflow-y-auto ${
                  mobileActiveSection === section.title && !mobileActiveSubsection
                    ? 'opacity-100 translate-x-0' 
                    : mobileActiveSection === section.title && mobileActiveSubsection
                    ? 'opacity-0 -translate-x-full'
                    : 'opacity-0 translate-x-full pointer-events-none'
                }`}
              >
                <button 
                  onClick={handleMobileBack}
                  className="flex items-center gap-2 text-sm text-primary py-3 border-b border-border/30"
                  style={{ fontWeight: 400 }}
                >
                  <ChevronLeft className="h-4 w-4 rotate-180" />
                  <span>{tm('חזרה')}</span>
                </button>
                <div className="text-center py-3 border-b border-border/30 text-primary font-semibold text-base">
                  {tm(section.title)}
                </div>
                {section.subsections.map((subsection, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setMobileActiveSubsection({ sectionTitle: section.title, subsectionIndex: idx })}
                    className="flex items-center py-3 border-b border-border/30 text-sm text-primary/80 hover:text-primary"
                    style={{ fontWeight: 400 }}
                  >
                    <span className="w-5 flex-shrink-0" />
                    <span className="flex-1 text-center">{tm(subsection.title || section.title)}</span>
                    <span className="w-5 flex-shrink-0 flex justify-center">
                      <ChevronLeft className="h-4 w-4" />
                    </span>
                  </button>
                ))}
              </div>
            ))}

            {/* Subsection Collections */}
            {menuSections.map((section) => (
              section.subsections.map((subsection, idx) => (
                <div 
                  key={`collections-${section.title}-${idx}`}
                  className={`flex flex-col px-4 py-4 transition-all duration-300 absolute inset-0 bg-background overflow-y-auto ${
                    mobileActiveSubsection?.sectionTitle === section.title && mobileActiveSubsection?.subsectionIndex === idx
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 translate-x-full pointer-events-none'
                  }`}
                >
                  <button 
                    onClick={handleMobileBack}
                    className="flex items-center gap-2 text-sm text-primary py-3 border-b border-border/30"
                    style={{ fontWeight: 400 }}
                  >
                    <ChevronLeft className="h-4 w-4 rotate-180" />
                    <span>{tm('חזרה')}</span>
                  </button>
                  <div className="text-center py-3 border-b border-border/30 text-primary font-semibold text-base">
                    {tm(subsection.title || section.title)}
                  </div>
                  {subsection.collections.map((collection) => (
                    <Link 
                      key={collection.node.id}
                      to={`/collections/${collection.node.handle}`}
                      onClick={handleMenuToggle}
                      className={`py-3 border-b border-border/30 text-sm text-primary/80 hover:text-primary flex items-center ${decodeURIComponent(location.pathname) === `/collections/${collection.node.handle}` ? 'font-semibold text-primary' : ''}`}
                      style={{ fontWeight: 400 }}
                    >
                      <span className="w-5 flex-shrink-0" />
                      <span className="flex-1 text-center">{subsection.collectionTitles?.[collection.node.handle] || tm(collection.node.title)}</span>
                      <span className="w-5 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              ))
            ))}
          </div>
        </nav>
      )}
    </header>
    </div>
    {/* Spacer to prevent content from being hidden under fixed header */}
    <div style={{ height: headerHeight > 0 ? headerHeight : undefined }} className={headerHeight > 0 ? '' : 'h-24 lg:h-36'} />
    </>
  );
};

export default Header;
